import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  Building2, 
  Search, 
  ExternalLink,
  CheckCircle,
  XCircle,
  Calendar as CalendarIcon,
  Users,
  RefreshCw,
  Loader2,
  Trash2,
  AlertTriangle,
  Settings,
  Plus,
  Minus,
  Clock,
  Ban,
  Sparkles,
  Activity
} from "lucide-react";
import { format, addDays, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { PlanBadge } from "@/components/PlanBadge";
import { BILLING_PLANS, getPlanLabel } from "@/lib/billingPlans";

interface EstablishmentStats {
  totalArrivedCount: number;
  totalNoShowCount: number;
  totalCancelledCount: number;
  totalReservations: number;
  hasDeletedData: boolean;
  lastDataDeletionAt: string | null;
  totalDeletedReservations: number;
  totalDeletedCustomers: number;
  lastActivityAt: string | null;
}

interface Establishment {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  plan_type: string | null;
  trial_ends_at: string | null;
  created_at: string;
  _count?: {
    reservations: number;
    users: number;
    customers: number;
    tables: number;
    timeSlots: number;
  };
  _stats?: EstablishmentStats;
}

interface OrphanUser {
  id: string;
  email: string;
}

export default function AdminEstablishments() {
  const { toast } = useToast();
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [establishmentToDelete, setEstablishmentToDelete] = useState<Establishment | null>(null);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Plan management dialog state
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null);
  const [newPlanType, setNewPlanType] = useState<string>("trial");
  const [newTrialEndDate, setNewTrialEndDate] = useState<Date | undefined>();
  const [savingPlan, setSavingPlan] = useState(false);

  // Create establishment dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEstName, setNewEstName] = useState("");
  const [newEstSlug, setNewEstSlug] = useState("");
  const [newEstEmail, setNewEstEmail] = useState("");
  const [assignAdmin, setAssignAdmin] = useState(false);
  const [adminMode, setAdminMode] = useState<'new' | 'existing'>('new');
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [orphanUsers, setOrphanUsers] = useState<OrphanUser[]>([]);
  const [loadingOrphanUsers, setLoadingOrphanUsers] = useState(false);

  // AI Credits dialog state
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [creditsEstablishment, setCreditsEstablishment] = useState<Establishment | null>(null);
  const [creditsData, setCreditsData] = useState<{ credits_used: number; credits_bonus: number } | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [savingCredits, setSavingCredits] = useState(false);
  const [bonusToAdd, setBonusToAdd] = useState(0);

  useEffect(() => {
    fetchEstablishments();
  }, []);

  const fetchEstablishments = async () => {
    try {
      setLoading(true);
      
      // Fetch establishments
      const { data: estData, error: estError } = await supabase
        .from('establishments')
        .select('*')
        .order('created_at', { ascending: false });

      if (estError) throw estError;

      // Helper: paginate to bypass Supabase 1000-row default limit
      const fetchAllEstablishmentIds = async (table: 'reservations' | 'user_roles' | 'customers' | 'tables' | 'time_slots'): Promise<{ establishment_id: string | null }[]> => {
        const all: { establishment_id: string | null }[] = [];
        const pageSize = 1000;
        let offset = 0;
        while (true) {
          const { data, error } = await supabase
            .from(table)
            .select('establishment_id')
            .range(offset, offset + pageSize - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          all.push(...data);
          if (data.length < pageSize) break;
          offset += pageSize;
        }
        return all;
      };

      // Fetch counts per establishment (paginated)
      const [resData, userData, customerData, tableData, timeSlotData] = await Promise.all([
        fetchAllEstablishmentIds('reservations'),
        fetchAllEstablishmentIds('user_roles'),
        fetchAllEstablishmentIds('customers'),
        fetchAllEstablishmentIds('tables'),
        fetchAllEstablishmentIds('time_slots'),
      ]);

      // Aggregate counts
      const createCountMap = (data: { establishment_id: string | null }[] | null) => {
        const map: Record<string, number> = {};
        data?.forEach(r => {
          if (r.establishment_id) {
            map[r.establishment_id] = (map[r.establishment_id] || 0) + 1;
          }
        });
        return map;
      };

      const resCountMap = createCountMap(resData);
      const userCountMap = createCountMap(userData);
      const customerCountMap = createCountMap(customerData);
      const tableCountMap = createCountMap(tableData);
      const timeSlotCountMap = createCountMap(timeSlotData);

      // Fetch detailed stats from edge function
      let statsMap: Record<string, EstablishmentStats> = {};
      try {
        const { data: statsResponse, error: statsError } = await supabase.functions.invoke('get-admin-establishment-stats');
        if (!statsError && statsResponse?.stats) {
          statsMap = statsResponse.stats;
        }
      } catch (err) {
        console.error('Error fetching establishment stats:', err);
      }

      const enrichedEstablishments = estData?.map(est => ({
        ...est,
        _count: {
          reservations: resCountMap[est.id] || 0,
          users: userCountMap[est.id] || 0,
          customers: customerCountMap[est.id] || 0,
          tables: tableCountMap[est.id] || 0,
          timeSlots: timeSlotCountMap[est.id] || 0,
        },
        _stats: statsMap[est.id] || undefined
      })) || [];

      setEstablishments(enrichedEstablishments);
    } catch (error) {
      console.error('Error fetching establishments:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les établissements.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveStatus = async (establishment: Establishment) => {
    setUpdatingId(establishment.id);
    try {
      const { error } = await supabase
        .from('establishments')
        .update({ is_active: !establishment.is_active })
        .eq('id', establishment.id);

      if (error) throw error;

      setEstablishments(prev => 
        prev.map(e => 
          e.id === establishment.id 
            ? { ...e, is_active: !e.is_active }
            : e
        )
      );

      toast({
        title: establishment.is_active ? "Établissement désactivé" : "Établissement activé",
        description: `${establishment.name} est maintenant ${establishment.is_active ? 'inactif' : 'actif'}.`,
      });
    } catch (error) {
      console.error('Error toggling status:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut.",
        variant: "destructive",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const openDeleteDialog = (establishment: Establishment) => {
    setEstablishmentToDelete(establishment);
    setConfirmName("");
    setDeleteDialogOpen(true);
  };

  // Plan management functions
  const openPlanDialog = (establishment: Establishment) => {
    setSelectedEstablishment(establishment);
    setNewPlanType(establishment.plan_type || 'trial');
    setNewTrialEndDate(establishment.trial_ends_at ? new Date(establishment.trial_ends_at) : addDays(new Date(), 30));
    setPlanDialogOpen(true);
  };

  const addTrialDays = (days: number) => {
    const baseDate = newTrialEndDate || new Date();
    setNewTrialEndDate(addDays(baseDate, days));
  };

  const resetTrial = (days: number = 30) => {
    setNewTrialEndDate(addDays(new Date(), days));
  };

  const getTrialDaysLeft = (date: Date | undefined) => {
    if (!date) return null;
    const now = new Date();
    return Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleSavePlan = async () => {
    if (!selectedEstablishment) return;
    setSavingPlan(true);

    try {
      const updateData: { plan_type: string; trial_ends_at: string | null } = {
        plan_type: newPlanType,
        trial_ends_at: newPlanType === 'trial' && newTrialEndDate
          ? newTrialEndDate.toISOString()
          : null,
      };

      const { error } = await supabase
        .from('establishments')
        .update(updateData)
        .eq('id', selectedEstablishment.id);

      if (error) throw error;

      setEstablishments(prev =>
        prev.map(e =>
          e.id === selectedEstablishment.id
            ? { ...e, ...updateData }
            : e
        )
      );

      toast({
        title: "Plan modifié",
        description: `Le plan de "${selectedEstablishment.name}" a été mis à jour.`,
      });

      setPlanDialogOpen(false);
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le plan.",
        variant: "destructive",
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDeleteEstablishment = async () => {
    if (!establishmentToDelete || confirmName !== establishmentToDelete.name) return;

    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Non authentifié');
      }

      const response = await supabase.functions.invoke('delete-establishment', {
        body: { establishmentId: establishmentToDelete.id }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Erreur lors de la suppression');
      }

      // Remove from local state
      setEstablishments(prev => prev.filter(e => e.id !== establishmentToDelete.id));
      
      toast({
        title: "Établissement supprimé",
        description: `"${establishmentToDelete.name}" et toutes ses données ont été supprimés définitivement.`,
      });

      setDeleteDialogOpen(false);
      setEstablishmentToDelete(null);
    } catch (error: any) {
      console.error('Error deleting establishment:', error);
      toast({
        title: "Erreur de suppression",
        description: error.message || "Impossible de supprimer l'établissement.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Create establishment functions
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const fetchOrphanUsers = async () => {
    setLoadingOrphanUsers(true);
    try {
      const response = await supabase.functions.invoke('manage-users', {
        body: { action: 'list' }
      });

      if (response.error) throw response.error;

      if (response.data?.users) {
        const orphans = response.data.users
          .filter((u: any) => !u.establishment_id && !u.is_super_admin)
          .map((u: any) => ({ id: u.id, email: u.email }));
        setOrphanUsers(orphans);
      }
    } catch (error) {
      console.error('Error fetching orphan users:', error);
      setOrphanUsers([]);
    } finally {
      setLoadingOrphanUsers(false);
    }
  };

  const openCreateDialog = () => {
    setNewEstName("");
    setNewEstSlug("");
    setNewEstEmail("");
    setAssignAdmin(false);
    setAdminMode('new');
    setNewAdminEmail("");
    setSelectedUserId("");
    setOrphanUsers([]);
    setCreateDialogOpen(true);
    fetchOrphanUsers();
  };

  const handleEstNameChange = (name: string) => {
    setNewEstName(name);
    setNewEstSlug(generateSlug(name));
  };

  const handleCreateEstablishment = async () => {
    if (!newEstName.trim() || !newEstSlug.trim()) {
      toast({ 
        title: "Erreur", 
        description: "Le nom et le slug sont requis.", 
        variant: "destructive" 
      });
      return;
    }

    // Validate admin options if selected
    if (assignAdmin) {
      if (adminMode === 'new' && !newAdminEmail.trim()) {
        toast({ 
          title: "Erreur", 
          description: "L'email du nouvel administrateur est requis.", 
          variant: "destructive" 
        });
        return;
      }
      if (adminMode === 'existing' && !selectedUserId) {
        toast({ 
          title: "Erreur", 
          description: "Veuillez sélectionner un utilisateur existant.", 
          variant: "destructive" 
        });
        return;
      }
    }

    setCreating(true);
    try {
      const payload: Record<string, any> = {
        establishmentName: newEstName.trim(),
        slug: newEstSlug.trim(),
        email: newEstEmail.trim() || null,
      };

      // Add admin options if selected
      if (assignAdmin) {
        if (adminMode === 'new' && newAdminEmail.trim()) {
          payload.adminEmail = newAdminEmail.trim();
        } else if (adminMode === 'existing' && selectedUserId) {
          payload.existingUserId = selectedUserId;
        }
      }

      const response = await supabase.functions.invoke('create-establishment', {
        body: payload
      });

      if (response.error) throw new Error(response.error.message);
      if (response.data?.error) throw new Error(response.data.error);

      let description = `"${newEstName}" a été créé avec succès.`;
      if (response.data?.adminAssigned) {
        if (response.data?.newUserCreated) {
          description += " Un email a été envoyé au nouvel administrateur.";
        } else {
          description += " L'administrateur a été assigné.";
        }
      }

      toast({
        title: "Établissement créé",
        description,
      });

      setCreateDialogOpen(false);
      fetchEstablishments();
    } catch (error: any) {
      console.error('Error creating establishment:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de créer l'établissement.",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  // AI Credits management
  const openCreditsDialog = async (est: Establishment) => {
    setCreditsEstablishment(est);
    setCreditsDialogOpen(true);
    setBonusToAdd(0);
    setLoadingCredits(true);
    
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { data, error } = await supabase.functions.invoke('manage-ai-credits', {
        body: { action: 'get', establishmentId: est.id, month: currentMonth }
      });
      
      if (error) throw error;
      setCreditsData(data?.credits || { credits_used: 0, credits_bonus: 0 });
    } catch (err) {
      console.error('Error fetching credits:', err);
      setCreditsData({ credits_used: 0, credits_bonus: 0 });
    } finally {
      setLoadingCredits(false);
    }
  };

  const handleAddBonusCredits = async () => {
    if (!creditsEstablishment || bonusToAdd <= 0) return;
    setSavingCredits(true);
    
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { error } = await supabase.functions.invoke('manage-ai-credits', {
        body: { 
          action: 'add-bonus', 
          establishmentId: creditsEstablishment.id, 
          month: currentMonth,
          bonusCredits: bonusToAdd 
        }
      });
      
      if (error) throw error;
      
      setCreditsData(prev => prev ? { ...prev, credits_bonus: prev.credits_bonus + bonusToAdd } : null);
      setBonusToAdd(0);
      
      toast({
        title: "Crédits ajoutés",
        description: `${bonusToAdd} crédits bonus ajoutés à "${creditsEstablishment.name}".`,
      });
    } catch (err) {
      console.error('Error adding credits:', err);
      toast({
        title: "Erreur",
        description: "Impossible d'ajouter les crédits.",
        variant: "destructive",
      });
    } finally {
      setSavingCredits(false);
    }
  };

  const handleResetCreditsUsage = async () => {
    if (!creditsEstablishment) return;
    setSavingCredits(true);
    
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { error } = await supabase.functions.invoke('manage-ai-credits', {
        body: { action: 'reset-usage', establishmentId: creditsEstablishment.id, month: currentMonth }
      });
      
      if (error) throw error;
      
      setCreditsData(prev => prev ? { ...prev, credits_used: 0 } : null);
      
      toast({
        title: "Crédits réinitialisés",
        description: `L'utilisation des crédits IA de "${creditsEstablishment.name}" a été remise à zéro.`,
      });
    } catch (err) {
      console.error('Error resetting credits:', err);
      toast({
        title: "Erreur",
        description: "Impossible de réinitialiser les crédits.",
        variant: "destructive",
      });
    } finally {
      setSavingCredits(false);
    }
  };

  const getCreditsLimit = (planType: string | null) => {
    return planType === 'premium' ? 50 : 5;
  };

  const filteredEstablishments = establishments.filter(est =>
    est.name.toLowerCase().includes(search.toLowerCase()) ||
    est.slug.toLowerCase().includes(search.toLowerCase()) ||
    est.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getTrialStatus = (trialEndsAt: string | null) => {
    if (!trialEndsAt) return null;
    const endDate = new Date(trialEndsAt);
    const now = new Date();
    const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { text: "Expiré", variant: "destructive" as const };
    if (daysLeft <= 3) return { text: `${daysLeft}j restant${daysLeft > 1 ? 's' : ''}`, variant: "warning" as const };
    return { text: `${daysLeft}j restants`, variant: "default" as const };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="container mx-auto p-6 max-w-7xl space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-3xl font-serif font-bold">Établissements</h1>
                <p className="text-muted-foreground">
                  {establishments.length} établissement{establishments.length > 1 ? 's' : ''} au total
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un établissement
              </Button>
              <Button onClick={fetchEstablishments} variant="outline" size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, slug ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardContent>
          </Card>

          {/* Establishments List */}
          <div className="space-y-4">
            {filteredEstablishments.map((est) => {
              const trialStatus = est.plan_type === 'trial' ? getTrialStatus(est.trial_ends_at) : null;
              const stats = est._stats;
              
              return (
                <Card key={est.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col gap-4 p-6">
                      {/* Top Row: Main Info + Actions */}
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        {/* Main Info */}
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-lg font-semibold">{est.name}</h3>
                            {est.is_active ? (
                              <Badge className="bg-green-500/10 text-green-600">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Actif
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <XCircle className="w-3 h-3 mr-1" />
                                Inactif
                              </Badge>
                            )}
                            <PlanBadge 
                              planType={est.plan_type} 
                              trialEndsAt={est.trial_ends_at}
                              showTooltip={false}
                              size="sm"
                            />
                            {/* Data Deleted Badge */}
                            {stats?.hasDeletedData && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="border-amber-500/50 text-amber-600 bg-amber-500/10">
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    Données effacées
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                  <div className="text-sm space-y-1">
                                    {stats.lastDataDeletionAt && (
                                      <p className="font-medium">
                                        Dernière suppression : {format(new Date(stats.lastDataDeletionAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                      </p>
                                    )}
                                    <p>{stats.totalDeletedReservations} réservation{stats.totalDeletedReservations > 1 ? 's' : ''} effacée{stats.totalDeletedReservations > 1 ? 's' : ''}</p>
                                    <p>{stats.totalDeletedCustomers} client{stats.totalDeletedCustomers > 1 ? 's' : ''} effacé{stats.totalDeletedCustomers > 1 ? 's' : ''}</p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            /{est.slug}
                          </p>
                          {est.email && (
                            <p className="text-sm text-muted-foreground">{est.email}</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3">
                          <a 
                            href={`/${est.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Voir
                            </Button>
                          </a>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {est.is_active ? 'Actif' : 'Inactif'}
                            </span>
                            <Switch
                              checked={est.is_active}
                              onCheckedChange={() => toggleActiveStatus(est)}
                              disabled={updatingId === est.id}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openCreditsDialog(est)}
                            title="Gérer les crédits IA"
                          >
                            <Sparkles className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openPlanDialog(est)}
                            title="Gérer le plan"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => openDeleteDialog(est)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Bottom Row: Stats */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border-t pt-4">
                        {/* Reservation Stats */}
                        {stats ? (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="font-medium">{stats.totalArrivedCount}</span>
                                  <span className="text-muted-foreground">arrivé{stats.totalArrivedCount > 1 ? 's' : ''}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Réservations où le client est arrivé</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 text-amber-600">
                                  <Clock className="w-4 h-4" />
                                  <span className="font-medium">{stats.totalNoShowCount}</span>
                                  <span className="text-muted-foreground">no-show</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Réservations planifiées dont la date est passée (client non arrivé)</TooltipContent>
                            </Tooltip>
                            
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 text-red-600">
                                  <Ban className="w-4 h-4" />
                                  <span className="font-medium">{stats.totalCancelledCount}</span>
                                  <span className="text-muted-foreground">annulé{stats.totalCancelledCount > 1 ? 's' : ''}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Réservations annulées</TooltipContent>
                            </Tooltip>

                            <div className="h-4 w-px bg-border" />

                            <div className="flex items-center gap-2 text-muted-foreground">
                              <CalendarIcon className="w-4 h-4" />
                              <span>{stats.totalReservations} résa total</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{est._count?.reservations || 0} résa</span>
                          </div>
                        )}

                        <div className="h-4 w-px bg-border" />

                        <Link 
                          to={`/admin/users?establishment=${est.id}`}
                          className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                          title={`Voir les utilisateurs de ${est.name}`}
                        >
                          <Users className="w-4 h-4" />
                          <span className="underline-offset-2 hover:underline">
                            {est._count?.users || 0} user{(est._count?.users || 0) > 1 ? 's' : ''}
                          </span>
                        </Link>

                        <div className="h-4 w-px bg-border" />

                        <div className="text-muted-foreground">
                          Créé {format(new Date(est.created_at), 'dd MMM yyyy', { locale: fr })}
                        </div>

                        {stats?.lastActivityAt && (
                          <>
                            <div className="h-4 w-px bg-border" />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1.5 text-muted-foreground">
                                  <Activity className="w-4 h-4" />
                                  <span>
                                    Actif {formatDistanceToNow(new Date(stats.lastActivityAt), { 
                                      addSuffix: true, 
                                      locale: fr 
                                    })}
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                Dernière activité : {format(new Date(stats.lastActivityAt), 'dd MMM yyyy à HH:mm', { locale: fr })}
                              </TooltipContent>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {filteredEstablishments.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {search ? 'Aucun établissement trouvé' : 'Aucun établissement'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <div className="flex items-center gap-3 text-destructive mb-2">
                <div className="p-2 bg-destructive/10 rounded-full">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <AlertDialogTitle className="text-xl">
                  Suppression définitive
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p className="text-base">
                    Vous êtes sur le point de supprimer définitivement l'établissement :
                  </p>
                  
                  <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg">
                    <p className="font-semibold text-foreground text-lg">
                      "{establishmentToDelete?.name}"
                    </p>
                    <p className="text-sm text-muted-foreground">/{establishmentToDelete?.slug}</p>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                    <p className="font-medium text-foreground">
                      Cette action supprimera IRRÉVERSIBLEMENT :
                    </p>
                    <ul className="text-sm space-y-1 ml-4">
                      <li>• {establishmentToDelete?._count?.reservations || 0} réservation(s)</li>
                      <li>• {establishmentToDelete?._count?.customers || 0} client(s)</li>
                      <li>• {establishmentToDelete?._count?.tables || 0} table(s)</li>
                      <li>• {establishmentToDelete?._count?.timeSlots || 0} créneau(x) horaire(s)</li>
                      <li>• {establishmentToDelete?._count?.users || 0} utilisateur(s) associé(s)</li>
                      <li>• Tous les logs d'emails</li>
                      <li>• Tous les paramètres et modèles</li>
                      <li>• L'établissement lui-même</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium">
                      Pour confirmer, tapez le nom exact de l'établissement :
                    </p>
                    <Input
                      placeholder={establishmentToDelete?.name}
                      value={confirmName}
                      onChange={(e) => setConfirmName(e.target.value)}
                      className="border-destructive/30 focus:border-destructive"
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteEstablishment}
                disabled={confirmName !== establishmentToDelete?.name || deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer définitivement
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Plan Management Dialog */}
        <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Gérer le plan
              </DialogTitle>
              <DialogDescription>
                {selectedEstablishment?.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Plan Type Selection */}
              <div className="space-y-2">
                <Label>Type de plan</Label>
                <Select value={newPlanType} onValueChange={setNewPlanType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BILLING_PLANS).map(([key, plan]) => (
                      <SelectItem key={key} value={key}>
                        {getPlanLabel(key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Trial Period Controls - Only visible for trial plan */}
              {newPlanType === 'trial' && (
                <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                  <Label className="text-base font-medium">Période d'essai</Label>

                  {/* Current Trial Info */}
                  <div className="text-sm space-y-1">
                    <p className="text-muted-foreground">
                      Date de fin : <span className="text-foreground font-medium">
                        {newTrialEndDate ? format(newTrialEndDate, 'dd MMMM yyyy', { locale: fr }) : 'Non définie'}
                      </span>
                    </p>
                    {newTrialEndDate && (
                      <p className={cn(
                        "font-medium",
                        getTrialDaysLeft(newTrialEndDate)! < 0 ? "text-destructive" :
                        getTrialDaysLeft(newTrialEndDate)! <= 3 ? "text-amber-600" :
                        "text-green-600"
                      )}>
                        {getTrialDaysLeft(newTrialEndDate)! < 0 
                          ? `Expiré depuis ${Math.abs(getTrialDaysLeft(newTrialEndDate)!)} jour(s)`
                          : `${getTrialDaysLeft(newTrialEndDate)} jour(s) restant(s)`
                        }
                      </p>
                    )}
                  </div>

                  {/* Quick Add Buttons */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Ajouter des jours</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => addTrialDays(7)}>
                        <Plus className="w-3 h-3 mr-1" />7j
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => addTrialDays(14)}>
                        <Plus className="w-3 h-3 mr-1" />14j
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => addTrialDays(30)}>
                        <Plus className="w-3 h-3 mr-1" />30j
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => addTrialDays(-7)}>
                        <Minus className="w-3 h-3 mr-1" />7j
                      </Button>
                    </div>
                  </div>

                  {/* Reset Trial Buttons */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Réinitialiser l'essai</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => resetTrial(14)}>
                        14j depuis maintenant
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => resetTrial(30)}>
                        30j depuis maintenant
                      </Button>
                    </div>
                  </div>

                  {/* Calendar Picker */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Ou choisir une date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newTrialEndDate ? format(newTrialEndDate, 'PPP', { locale: fr }) : 'Sélectionner'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={newTrialEndDate}
                          onSelect={(date) => date && setNewTrialEndDate(date)}
                          initialFocus
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPlanDialogOpen(false)} disabled={savingPlan}>
                Annuler
              </Button>
              <Button onClick={handleSavePlan} disabled={savingPlan}>
                {savingPlan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Establishment Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Créer un établissement
              </DialogTitle>
              <DialogDescription>
                Créez un nouvel établissement et assignez éventuellement un administrateur.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="est-name">Nom de l'établissement *</Label>
                  <Input
                    id="est-name"
                    placeholder="Mon Restaurant"
                    value={newEstName}
                    onChange={(e) => handleEstNameChange(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="est-slug">Slug (URL) *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">/</span>
                    <Input
                      id="est-slug"
                      placeholder="mon-restaurant"
                      value={newEstSlug}
                      onChange={(e) => setNewEstSlug(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="est-email">Email de contact</Label>
                  <Input
                    id="est-email"
                    type="email"
                    placeholder="contact@monrestaurant.ch"
                    value={newEstEmail}
                    onChange={(e) => setNewEstEmail(e.target.value)}
                  />
                </div>
              </div>

              {/* Admin Assignment */}
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="assign-admin"
                    checked={assignAdmin}
                    onCheckedChange={(checked) => setAssignAdmin(checked === true)}
                  />
                  <Label htmlFor="assign-admin" className="font-medium cursor-pointer">
                    Assigner un administrateur
                  </Label>
                </div>

                {assignAdmin && (
                  <div className="space-y-4 pl-6">
                    <RadioGroup value={adminMode} onValueChange={(v) => setAdminMode(v as 'new' | 'existing')}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="new" id="admin-new" />
                        <Label htmlFor="admin-new" className="cursor-pointer">Créer un nouvel utilisateur</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="existing" id="admin-existing" />
                        <Label htmlFor="admin-existing" className="cursor-pointer">Utiliser un utilisateur existant</Label>
                      </div>
                    </RadioGroup>

                    {adminMode === 'new' && (
                      <div className="space-y-2">
                        <Label htmlFor="admin-email">Email du nouvel administrateur</Label>
                        <Input
                          id="admin-email"
                          type="email"
                          placeholder="admin@example.com"
                          value={newAdminEmail}
                          onChange={(e) => setNewAdminEmail(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Un email sera envoyé pour créer le compte.
                        </p>
                      </div>
                    )}

                    {adminMode === 'existing' && (
                      <div className="space-y-2">
                        <Label>Sélectionner un utilisateur</Label>
                        {loadingOrphanUsers ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Chargement...
                          </div>
                        ) : orphanUsers.length > 0 ? (
                          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Choisir un utilisateur..." />
                            </SelectTrigger>
                            <SelectContent>
                              {orphanUsers.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  {user.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Aucun utilisateur sans établissement disponible.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
                Annuler
              </Button>
              <Button onClick={handleCreateEstablishment} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Credits Management Dialog */}
        <Dialog open={creditsDialogOpen} onOpenChange={setCreditsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Crédits IA
              </DialogTitle>
              <DialogDescription>
                {creditsEstablishment?.name}
              </DialogDescription>
            </DialogHeader>

            {loadingCredits ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <div className="space-y-6 py-4">
                {/* Current Credits Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-2xl font-bold">
                      {creditsData?.credits_used ?? 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Utilisés ce mois</p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg text-center">
                    <p className="text-2xl font-bold">
                      {getCreditsLimit(creditsEstablishment?.plan_type ?? null) + (creditsData?.credits_bonus ?? 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Total disponible</p>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p>Plan: <strong>{creditsEstablishment?.plan_type || 'trial'}</strong> ({getCreditsLimit(creditsEstablishment?.plan_type ?? null)} crédits/mois)</p>
                  {(creditsData?.credits_bonus ?? 0) > 0 && (
                    <p>Bonus ajoutés: <strong>{creditsData?.credits_bonus}</strong></p>
                  )}
                </div>

                {/* Add Bonus Credits */}
                <div className="space-y-2">
                  <Label>Ajouter des crédits bonus</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={bonusToAdd}
                      onChange={(e) => setBonusToAdd(Math.max(0, parseInt(e.target.value) || 0))}
                      placeholder="Nombre de crédits"
                    />
                    <Button 
                      onClick={handleAddBonusCredits} 
                      disabled={bonusToAdd <= 0 || savingCredits}
                    >
                      {savingCredits ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setBonusToAdd(5)}>+5</Button>
                    <Button variant="outline" size="sm" onClick={() => setBonusToAdd(10)}>+10</Button>
                    <Button variant="outline" size="sm" onClick={() => setBonusToAdd(20)}>+20</Button>
                  </div>
                </div>

                {/* Reset Usage */}
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={handleResetCreditsUsage}
                    disabled={savingCredits || (creditsData?.credits_used ?? 0) === 0}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Réinitialiser l'utilisation
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditsDialogOpen(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
